import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppPath } from '@app/app-path';
import { Logger } from '@core/services/logger.service';
import { UserService } from '@core/services/user.service';
import { ActivateUserNavigationState } from '@shared/models/activate-user-navigation-state';
import { LoadingOverlayService } from '@shared/services/loading-overlay.service';
import { PasswordService } from '@shared/services/password.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
	selector: 'cass-activate-user-page',
	templateUrl: './activate-user-page.component.html',
	styleUrls: ['./activate-user-page.component.css']
})
export class ActivateUserPageComponent implements OnDestroy {

	private readonly COMPONENT_NAME = "ActivateUserPageComponent";

	registeringUserEmail: string | undefined;
	activateUserForm: FormGroup = new FormGroup({});

	protected isPasswordShown = false;

	private callingOut$ = new BehaviorSubject<boolean>(false);

	private _subs = new SubSink();
	constructor (
		private _loadingOverlayService: LoadingOverlayService,
		private _passwordService: PasswordService,
		private _userService: UserService,
		private _alertService: UserAlertService,
		private _router: Router,
		private _logger: Logger
	) {
		this.initComponent();
	}

	ngOnDestroy(): void {
		this._subs.unsubscribe();
	}

	private initComponent(): void {

		const nav = this._router.getCurrentNavigation();
		if (nav && nav.extras && nav.extras.state) {
			const state = nav.extras.state as ActivateUserNavigationState;
			this.registeringUserEmail = state.email;
		}

		this._subs.add(
			this.callingOut$
				.pipe(
					distinctUntilChanged()
				)
				.subscribe((isCallingOut: boolean) => {
					if (isCallingOut) {
						this._loadingOverlayService.showIndeterminateHorizontalProgressIndicator();
					}
					else {
						this._loadingOverlayService.hide();
					}
				}));

		this.activateUserForm.addControl(
			"verificationCode", new FormControl<string>("", [Validators.required]) // use Validators.pattern('^[0-9]*$') for digits only
		);

		if (!this.registeringUserEmail) {
			this.activateUserForm.addControl("email", new FormControl<string>("", [Validators.required, Validators.email, Validators.maxLength(254)]));

			const newPasswordControl = new FormControl<string>("", [Validators.required, Validators.minLength(this._passwordService.MIN_PASS_LENGTH)]);

			this.activateUserForm.addControl("newPassword", newPasswordControl);

			this.activateUserForm.addControl("confirmPassword", new FormControl<string>("", [
				Validators.required,
				Validators.minLength(this._passwordService.MIN_PASS_LENGTH),
				this._passwordService.confirmPasswordValidator(newPasswordControl)
			]));
		}
	}

	activateAction(): void {
		if (!this.activateUserForm.valid) {
			// trigger display of all field validation errors
			this.activateUserForm.markAllAsTouched();
			return;
		}

		const email = this.registeringUserEmail || this.activateUserForm.get("email")?.value;
		const verificationCode = this.activateUserForm.get("verificationCode")?.value;
		const password = this.activateUserForm.get("newPassword")?.value || "";

		this.callingOut$.next(true);
		this._userService.activateUser(email, verificationCode, password).subscribe({
			next: () => {
				this.callingOut$.next(false);
				this._router.navigate([AppPath.AccountCreated] /*, { state: state }*/);
			},
			error: (error) => {
				this.callingOut$.next(false);
				this._alertService.errorByCassError(error);
			}
		});
	}
}
